import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Calendar, 
  FileText, 
  Shield, 
  CreditCard, 
  Users, 
  ChevronRight, 
  Check, 
  BarChart, 
  GraduationCap,
  X,
  ChevronDown,
  ChevronUp,
  BrainCircuit,
  Zap,
  Settings,
  FunctionSquare,
  LayoutDashboard
} from 'lucide-react';
import { Link } from 'react-router-dom';
import AIChatBox from '../AIChatBox';
import ROICalculator from '../ai/ROICalculator';

// FAQ Accordion Item Component
const FaqAccordionItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="bg-slate-800/70 border border-slate-700/50 rounded-xl mb-4 overflow-hidden">
      <button
        className="w-full text-left px-6 py-4 flex items-center justify-between focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <h4 className="font-medium text-white text-base">{question}</h4>
        <div className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown className="w-5 h-5 text-blue-400" />
        </div>
      </button>
      <div 
        className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 pb-6' : 'max-h-0 pb-0'
        }`}
      >
        <div className="text-slate-300 text-sm leading-relaxed" 
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      </div>
    </div>
  );
};

const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show button when page is scrolled down
    const toggleVisibility = () => {
      if (window.pageYOffset > 500) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <button
      onClick={scrollToTop}
      className={`fixed bottom-8 right-8 p-3 rounded-full bg-blue-600 text-white shadow-lg transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      } z-50`}
      aria-label="Scroll to top"
    >
      <ChevronUp className="h-6 w-6" />
    </button>
  );
};
// FAQ Data
const faqItems = {
  general: [
    {
      question: "What is Nia?",
      answer: "Nia is an AI sales assistant that helps businesses qualify leads, schedule meetings, send custom quotes, and even process payments automatically, 24/7. Built on Google Cloud and powered by advanced AI, Nia acts as your always-on sales representative."
    },
    {
      question: "How does Nia differ from ChatGPT or other AI chatbots?",
      answer: "Unlike generic AI chatbots, Nia is specifically designed for sales processes with built-in functionality to schedule meetings in your calendar, verify emails, generate and send quotes, and process transactions. Nia also uses Retrieval Augmented Generation (RAG) to access your specific business information, ensuring accurate responses about your products and services."
    },
    {
      question: "Can Nia replace my sales team?",
      answer: "Nia is designed to complement your existing sales team, not replace it. Nia handles repetitive tasks like initial lead qualification, meeting scheduling, and quote generation, freeing your human sales representatives to focus on relationship building and closing deals."
    },
    {
      question: "Can I try Nia before committing to the full service?",
      answer: "Yes! We offer a 14-day free trial with no credit card required. You can sign up, customise your AI assistant, and upload your sales materials to see how Nia works with your business. After the trial period, you can choose to continue with our outcome-based pricing model or cancel with no obligation."
    }
  ],
  ecosystem: [
    {
      question: "What is Atlas and how does it work with Nia?",
      answer: "Atlas is Nia's strategic counterpart, a Senior Sales Strategist AI designed to analyse customer conversations, develop sophisticated sales approaches, and execute winning strategies to close deals with optimal profit margins. While Nia handles customer-facing interactions, Atlas works behind the scenes to develop and refine your sales approach, providing your team with actionable insights and strategic recommendations."
    },
    {
      question: "What is BrainCache and how does it enhance sales conversations?",
      answer: "BrainCache is Nia's structured memory system that maintains critical contextual information about customer conversations. It captures and organises customer details, business goals, IT requirements, and interaction history. This specialised system enables Nia to remember previous interactions, prevent duplicate actions (like scheduling the same meeting twice), and create coherent conversations across multiple sessions—resulting in a more natural and effective sales experience."
    },
    {
      question: "What is the Strategy Hub and who can access it?",
      answer: "The Strategy Hub is a sophisticated dashboard interface for your sales and strategy teams. It provides real-time access to customer conversations, BrainCache data, and Atlas's strategic analysis. Team members can review conversations, analyse customer needs, collaborate on sales strategies, and assign follow-up tasks directly through the interface. The Strategy Hub is accessible only to authorised team members with appropriate roles (sales, strategy, or admin)."
    },
    {
      question: "How does the Dashboard help me monitor Nia's performance?",
      answer: "Nia's comprehensive Dashboard provides real-time analytics and visualisation of your sales activities. Track metrics like total conversations, meeting conversion rates, quote generation, and sales funnel progression. The Dashboard offers insights into customer engagement patterns, response times, and qualification rates, helping you identify optimisation opportunities and measure ROI from your AI sales team."
    }
  ],
  technical: [
    {
      question: "How does Nia integrate with my website?",
      answer: "Nia can be integrated with your existing website using our JavaScript widget. The setup process is handled during the professional services deployment, requiring minimal technical involvement from your team."
    },
    {
      question: "Is Nia secure and compliant with data protection regulations?",
      answer: "Yes, Nia is built on Google Cloud's enterprise-grade security infrastructure. All conversations are encrypted, and we implement strict access controls. Customer data is handled in compliance with GDPR and other relevant data protection regulations."
    },
    {
      question: "What technologies power Nia?",
      answer: "Nia is powered by Google's Gemini AI model with a sophisticated Retrieval Augmented Generation (RAG) implementation to prevent hallucinations. The system runs on Google Cloud Functions with Firebase for reliable infrastructure, offering 99.99% uptime and automatic scaling."
    }
  ],
  functionality: [
    {
      question: "How does the BrainCache CRM update work?",
      answer: "Nia's BrainCache CRM update happens naturally during conversations. As customers share information about their business, goals, or requirements, Nia automatically extracts and organises this data in the BrainCache without explicitly asking for it. This creates a comprehensive customer profile that improves over time, allowing for increasingly personalised interactions while maintaining a natural conversation flow."
    },
    {
      question: "Can Atlas analyse email conversations with customers?",
      answer: "Yes, Atlas can process email threads from Gmail to provide strategic analysis. By integrating with your email, Atlas can read customer correspondence, analyse sentiment and intent, and provide recommendations for follow-up actions. This feature bridges the gap between email communications and your AI sales strategy, ensuring consistent messaging across all channels."
    },
    {
      question: "What sales tasks can Nia handle?",
      answer: "Nia can:<br>- Engage with website visitors 24/7<br>- Qualify leads using BANT criteria<br>- Answer product and pricing questions<br>- Schedule consultations directly in your Google Calendar<br>- Generate and email custom quotes<br>- Send Paypal payment links via email along with the quote<br>- Maintain conversation context across user sessions"
    },
    {
      question: "How does Nia schedule meetings?",
      answer: "When a prospect requests a meeting, Nia verifies their email address, collects their preferred date and time, and automatically schedules the meeting in your Google Calendar. The prospect receives a confirmation email with the Google Meet link."
    },
    {
      question: "Can Nia generate custom quotes?",
      answer: "Yes, Nia can create detailed, custom quotes based on your prospect's requirements. These quotes include itemised services, costs, VAT calculations, and your company terms and conditions. Once generated, Nia can email the quote directly to your prospect."
    }
  ],
  business: [
    {
      question: "How much does Nia cost?",
      answer: "Nia uses an outcome-based pricing model:<br>1. One-time setup fee (from £999) for professional deployment<br>2. Infrastructure costs based on actual usage (scales to zero when idle)<br>3. Success commission: 15% on the customer's first invoice and 8% on future annual billings"
    },
    {
      question: "What ROI can I expect from implementing Nia?",
      answer: "Businesses implementing Nia typically see:<br>- 10x faster deal closing<br>- 24/7 lead qualification without increasing headcount<br>- 70% reduction in administrative work<br>- Increased customer satisfaction with instant response times<br>- More consistent sales messaging across all customer interactions"
    },
    {
      question: "Is there a minimum contract period?",
      answer: "No, there's no minimum contract period. Since our pricing is success-based, we only succeed when you do."
    }
  ],
  implementation: [
    {
      question: "How does the Strategy Dashboard help sales managers?",
      answer: "The Strategy Dashboard gives sales managers unprecedented visibility into customer interactions and sales opportunities. Managers can:<br>- Review complete conversation histories<br>- analyse customer information and requirements<br>- View Atlas's strategic recommendations<br>- Assign follow-up actions to team members<br>- Track key performance metrics<br>- Process and analyse customer emails"
    },
    {
      question: "How long does it take to set up Nia?",
      answer: "The initial setup typically takes 2-3 days, including:<br>- Integration with your website<br>- Configuration of your Google Cloud Platform project<br>- Creation of your sales knowledge base<br>- Testing and optimisation"
    },
    {
      question: "What information do I need to provide to customise Nia?",
      answer: "To personalise Nia for your business, you'll need to provide:<br>- Product information and pricing<br>- Service descriptions<br>- Frequently asked questions<br>- Company policies and terms<br>- Team calendar access for scheduling"
    },
    {
      question: "Can I customise Nia's appearance and behavior?",
      answer: "Yes, Nia can be customised to match your brand's visual identity and communication style. During the setup process, we'll work with you to ensure Nia represents your brand appropriately."
    }
  ],
  future: [
    {
      question: "What new features are coming to Nia?",
      answer: "Our roadmap includes:<br>- Automated follow-up sequences<br>- Voice calling capabilities<br>- Personal email address for Nia<br>- Enhanced CRM integrations with popular platforms<br>- Advanced analytics and reporting"
    },
    {
      question: "How often is Nia updated?",
      answer: "Nia is constantly evolving with regular updates. As a cloud-based solution, all updates are deployed automatically without requiring any action from your team."
    },
    {
      question: "Will Nia work with my specific industry or business model?",
      answer: "Nia is highly adaptable and can be customised for various industries and business models. During our initial consultation, we'll assess your specific requirements and confirm Nia's suitability for your business."
    }
  ]
};

const NiaProductPage = () => {
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessage, setChatMessage] = useState('');

  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Remove any existing schema script if present
      const existingScript = document.getElementById('faq-schema');
      if (existingScript) {
        existingScript.remove();
      }
  
      // Create new schema script
      const script = document.createElement('script');
      script.id = 'faq-schema';
      script.type = 'application/ld+json';
      
      // Prepare FAQ schema data
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": []
      };
      
      // Add all FAQ items to the schema
      Object.values(faqItems).forEach(category => {
        category.forEach(item => {
          faqSchema.mainEntity.push({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer.replace(/<br>/g, " ") // Remove HTML tags for schema
            }
          });
        });
      });
      
      script.innerHTML = JSON.stringify(faqSchema);
      document.head.appendChild(script);
    }
    }, []);

  const handleChatWithNia = (topic) => {
    let message = '';
    switch(topic) {
      case 'general':
        message = "Hi Nia, I'd like to learn more about the AI Sales Assistant and how it can help with our sales process.";
        break;
      case 'schedule':
        message = "Hi Nia, I'm interested in scheduling a consultation to learn more about AI Sales Assistant.";
        break;
      case 'workspace':
        message = "Hi Nia, I need help comparing Google Workspace plans for my business. Could you advise me?";
        break;
      case 'quote':
        message = "Hi Nia, I'm interested in getting a quote for AI Sales Assistant.";
        break;
      default:
        message = "Hi Nia, I'd like to learn more about your capabilities.";
    }
    setShowChatModal(true);
    setChatMessage(message);
  };
  return (
    <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-32 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-slate-950 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-500/10 rounded-full px-4 py-2 mb-8">
                <Shield className="w-5 h-5 text-blue-400" />
                <span className="text-blue-400">Powered by Gemini GenAI & Google Cloud</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Meet <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Nia & Atlas</span>, Your AI Sales Team
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                Supercharge your sales with gClouds' multi-agent Salesforce Ecosystem. Nia engages with customers 24/7 while automatically creating your CRM, Atlas provides strategic insights, and your team manages everything through the Strategy Hub.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="#demo" 
                  onClick={() => handleChatWithNia('general')}
                  className="px-8 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors text-center"
                >
                  Try Demo
                </a>
                {/* Sign Up for Free Trial                */}
                <Link 
                  to="/nia-signup" 
                  className="px-8 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-medium transition-colors text-center"
                >
                  Sign Up for Free Trial
                </Link>

                <a 
                  href="#pricing" 
                  className="px-8 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium transition-colors text-center"
                >
                  View Pricing
                </a>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-slate-800/70 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                {/* Video element */}
                <video 
                  className="w-full h-auto"
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                >
                  <source src="/videos/Salesforce-AI-agent-4.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Transform Your Entire Sales Process</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Our AI Sales Ecosystem handles every aspect of your sales process, from initial lead engagement to strategic decision making.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <MessageSquare className="w-6 h-6" />,
                title: "Nia: Your 24/7 Sales Representative",
                description: "Nia engages with prospects, answers product questions, and qualifies leads using BANT criteria and simultaneously builds your CRM database during natural conversations."
              },
              {
                icon: <FileText className="w-6 h-6" />,
                title: "Custom Quote Generation",
                description: "Generate and email detailed quotes with your branding. Nia can create tailored quotes based on customer needs and send them directly."
              },
              {
                icon: <Calendar className="w-6 h-6" />,
                title: "Automated Meeting Scheduling",
                description: "Let Nia coordinate and book meetings between prospects and your sales team directly in Google Calendar."
              },
              {
                icon: <CreditCard className="w-6 h-6" />,
                title: "Streamlined Purchase Process",
                description: "Nia sends custom purchase links with your payment gateway, making it easy for customers to complete transactions."
              },
              {
                icon: <BrainCircuit className="w-6 h-6" />,
                title: "Atlas: Your Strategic Sales Analyst",
                description: "Atlas works behind the scenes to analyse customer conversations, develop sophisticated sales approaches, and execute winning strategies with your team."
              },
              {
                icon: <LayoutDashboard className="w-6 h-6" />,
                title: "Strategy Hub: Your Command Center",
                description: "Access real time analytics, customer insights from your automatically created CRM, and strategic recommendations through a comprehensive dashboard for your team."
              }
            ].map((feature, index) => (
              <div 
                key={index} 
                className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 hover:border-blue-500/50 transition-colors"
              >
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center text-blue-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-slate-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How Our AI Sales Ecosystem Works</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Powered by Google's advanced AI, our ecosystem seamlessly integrates with your entire sales process.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-20">
            <div className="bg-slate-800 rounded-xl overflow-hidden shadow-xl">
              <img 
                src="images/nia_dashboard1024x1024.png" 
                alt="Nia Dashboard Preview" 
                className="w-full h-auto"
              />
            </div>
            
            <div className="space-y-8">
              {[
                {
                  number: "01",
                  title: "Connect Nia to your website",
                  description: "Simple integration with your existing website using our JavaScript widget."
                },
                {
                  number: "02",
                  title: "Customise your sales knowledge base",
                  description: "Upload your product information, pricing, and FAQs to personalise Nia for your business."
                },
                {
                  number: "03",
                  title: "Nia engages with prospects",
                  description: "When visitors have questions, Nia provides accurate information and qualifies their interest. Customer information is intelligently captured and organised into a structured CRM system in real-time."
                },
                {
                  number: "04",
                  title: "Access insights via the Strategy Hub",
                  description: "Your team can review conversations, scheduled meetings, and sent quotes, analyse CRM data, and implement Atlas’s strategic recommendations, all through an intuitive dashboard."
                }
              ].map((step, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center font-bold flex-shrink-0">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-slate-300">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="py-20 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-8">The Benefits of a Complete AI Sales Ecosystem</h2>
              
              <div className="space-y-6">
                {[
                  {
                    title: "Frictionless CRM Development",
                    description: "Your CRM builds itself automatically during natural customer conversations, eliminating manual data entry while capturing critical information."
                  },
                  {
                    title: "24/7 Lead Qualification",
                    description: "Never miss a sales opportunity with round-the-clock lead engagement and qualification."
                  },
                  {
                    title: "Reduced Response Time",
                    description: "Instant responses to prospect inquiries increase engagement and conversion rates."
                  },
                  {
                    title: "Scalable Sales Operations",
                    description: "Handle more leads without increasing headcount, allowing your team to focus on high-value activities."
                  },
                  {
                    title: "Centralised Strategy Management",
                    description: "The Strategy Hub gives your team full visibility into customer conversations, CRM data, and Atlas's strategic insights in one unified dashboard."
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="p-1 bg-blue-500/10 rounded-full text-blue-400 flex-shrink-0 mt-1">
                      <Check className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-1">{benefit.title}</h3>
                      <p className="text-slate-300">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-transparent blur-3xl rounded-3xl"></div>
              <div className="relative bg-slate-800 rounded-xl overflow-hidden border border-slate-700/50 shadow-xl">
                <div className="p-8">
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="text-2xl font-bold">Performance Impact</h3>
                    <span className="bg-blue-500/10 text-blue-400 px-3 py-1 rounded-full text-sm font-medium">Average Results</span>
                  </div>
                  
                  <div className="space-y-6">
                    {[
                      { metric: "Lead Response Time", value: "< 1 minute", improvement: "↓ 98%" },
                      { metric: "Qualified Leads", value: "3x increase", improvement: "↑ 200%" },
                      { metric: "Sales Cycle Duration", value: "30% shorter", improvement: "↓ 30%" },
                      { metric: "Customer Satisfaction", value: "92% positive", improvement: "↑ 15%" },
                      { metric: "Sales Team Capacity", value: "40% more time", improvement: "↑ 40%" }
                    ].map((stat, index) => (
                      <div key={index} className="flex items-center justify-between pb-4 border-b border-slate-700/50">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          <span className="text-slate-200">{stat.metric}</span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="font-semibold">{stat.value}</span>
                          <span className={`text-sm ${stat.improvement.includes('↑') ? 'text-green-400' : 'text-blue-400'}`}>
                            {stat.improvement}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
        {/* Pricing Section */}
        <section id="pricing" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Outcome-Based Pricing: You Win, We Win</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            A transparent partnership model with no hidden fees
            </p>
            </div>
            
            <div className="relative">
                <div className="absolute inset-0 bg-blue-500/10 rounded-xl -mx-4 -my-4 blur-xl"></div>
                <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-800/50 border border-slate-700/50 rounded-xl p-8">
                    {[
                        {
                            step: "01",
                            title: "One-Time Setup",
                            description: "From £999, Professional Services deployment of Nia",
                            details: [
                                "Nia website integration",
                                "Google Cloud Platform Project configuration",
                                "Sales knowledge base creation"
                            ]
                        },
                        {
                            step: "02",
                            title: "Nia Finds Leads",
                            description: "Automatically engage and qualify potential customers through intelligent conversation",
                            details: [
                                "24/7 prospect interaction",
                                "BANT qualification criteria",
                                "Contextual lead scoring"
                            ]
                        },
                        {
                            step: "03",
                            title: "Infrastructure",
                            description: "Pay only for actual usage",
                            details: [
                                "Zero cost during low traffic",
                                "No minimum monthly charge",
                                "Scales directly with usage",
                                "Full Google Cloud Platform billing transparency",
                                "Predictable cost model"
                            ]
                            },
                        {
                            step: "04",
                            title: "Customer Conversion",
                            description: "Transform qualified leads into paying customers",
                            details: [
                                "Product recommendations",
                                "Streamlined purchase process",
                                "Instant quote generation"
                            ]
                        },
                        {
                            step: "05",
                            title: "Invoice Generation",
                            description: "Customer completes purchase and you send your first invoice",
                            details: [
                                "Clear sales attribution",
                                "Transparent invoicing",
                                "Verifiable sales tracking"
                            ]
                        },
                        {
                            step: "06",
                            title: "Success Commission",
                            description: "15% → 8% Commission from Nia-generated sales",
                            details: [
                                "15% on your customer's first invoice",
                                "8% from your customer's future billings",
                                "No commission on unattributed sales"
                            ]
                            }
                    ].map((item, index) => (
                        <div 
                            key={index} 
                            className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-6"
                        >
                            <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center text-blue-400 mb-4 font-bold">
                                {item.step}
                            </div>
                            <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                            <p className="text-slate-300 mb-4">{item.description}</p>
                            
                            <ul className="space-y-2">
                                {item.details.map((detail, i) => (
                                    <li key={i} className="flex items-start space-x-2">
                                        <Check className="w-4 h-4 text-blue-400 flex-shrink-0 mt-1" />
                                        <span className="text-slate-400 text-sm">{detail}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
            <div id="roi" className="mt-12 text-center">
            <ROICalculator />
            </div>
            
            <div className="mt-8 text-center">
            <p className="text-slate-300">
                Ready to transform your sales process? <a href="#contact" className="text-blue-400 hover:underline">Schedule a consultation</a>
            </p>
            </div>
        </div>
        </section>
      
      {/* Demo Section with Embedded AIChatBox */}
      <section id="demo" className="py-20 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Try Nia Today</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Experience the power of AI-assisted sales with a live demo of Nia.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2 flex flex-col" style={{ height: "500px" }}>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700/50 h-[280px] mb-5">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <GraduationCap className="w-5 h-5 mr-2 text-blue-400" />
                  How to use this demo
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center flex-shrink-0 mt-0.5">1</div>
                    <p>Ask Nia about Google Cloud products and pricing</p>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center flex-shrink-0 mt-0.5">2</div>
                    <p>Request a custom quote for your business needs</p>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center flex-shrink-0 mt-0.5">3</div>
                    <p>Try scheduling a consultation meeting</p>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center flex-shrink-0 mt-0.5">4</div>
                    <p>Compare different Google Workspace plans</p>
                  </li>
                </ul>
              </div>
              
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700/50 h-[280px]">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-blue-400" />
                  The Power of AI in Sales
                </h3>
                <div className="space-y-4">
                  <div className="pb-4 border-b border-slate-700/50">
                    <p className="italic text-slate-300 mb-2">"In our initial deployment, Nia has already helped us generate £2,500 in potential sales, demonstrating the promising potential of AI-assisted sales."</p>
                    <p className="text-sm text-slate-400">— M Gulercan, Director at gClouds</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-3">
              {showChatModal ? (
                <div className="bg-slate-800 rounded-xl border border-slate-700/50 overflow-hidden h-full">
                  <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <span className="font-bold text-blue-400">N</span>
                      </div>
                      <div>
                        <h3 className="font-medium">Nia Demo</h3>
                        <p className="text-xs text-slate-400">Live Interactive Demo</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowChatModal(false)}
                      className="text-slate-400 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                    <AIChatBox 
                      initialMessageModal={chatMessage || "Hi! I'd like to learn more about how Nia can help with our sales process."} 
                    />
                  </div>
              ) : (
                <div className="bg-slate-800 rounded-xl border border-slate-700/50 overflow-hidden h-full flex flex-col">
                  <div className="p-4 border-b border-slate-700 flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <span className="font-bold text-blue-400">N</span>
                    </div>
                    <div>
                      <h3 className="font-medium">Nia Demo</h3>
                      <p className="text-xs text-slate-400">Live Interactive Demo</p>
                    </div>
                  </div>
                  
                  <div className="flex-1 p-6 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="w-10 h-10 text-blue-400" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">Try Nia Live Demo</h3>
                      <p className="text-slate-300 mb-6">Experience how Nia can transform your sales process</p>
                      <button 
                        onClick={() => {
                          setShowChatModal(true);
                          setChatMessage("");
                        }}
                        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
                      >
                        Start Demo
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Contact Section - Using Nia Instead of Form */}
      <section id="contact" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to transform your sales process?</h2>
              <p className="text-xl text-slate-300 mb-8">
                Talk to Nia directly to experience how she can transform your sales process. She'll guide you through scheduling a demo or answering any questions you might have.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Chat with Nia</h3>
                    <p className="text-slate-300 mb-2">Nia can answer your questions, provide a live demo now, or schedule a consultation with our team.</p>
                    <a 
                      href="#demo"
                      onClick={() => handleChatWithNia('general')}
                      className="text-blue-400 hover:text-blue-300 transition-colors font-medium inline-flex items-center"
                    >
                      Try Nia now <ChevronRight className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Schedule via Nia</h3>
                    <p className="text-slate-300 mb-2">Let Nia book a consultation for you with our Google Cloud specialists.</p>
                    <a
                      href="#demo" 
                      onClick={() => handleChatWithNia('schedule')}
                      className="text-blue-400 hover:text-blue-300 transition-colors font-medium inline-flex items-center"
                    >
                      Schedule now <ChevronRight className="w-4 h-4 ml-1" />
                      </a>
                  </div>
                </div>
                {/*    Signup             */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 text-green-400 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Get Started with Free Trial</h3>
                    <p className="text-slate-300 mb-2">Sign up for a 14-day free trial and start using Nia on your website.</p>
                    <Link 
                      to="/nia-signup"
                      className="text-green-400 hover:text-green-300 transition-colors font-medium inline-flex items-center"
                    >
                      Start free trial <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </div>


              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-6 flex justify-center">
                <img 
                  src="/images/nia_in_action_24hr512x358.png" 
                  alt="Nia available 24/7 across all devices" 
                  className="max-w-full h-auto rounded-lg"
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Why use Nia for sales?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span>Available 24/7 to engage with prospects</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span>Handles repetitive tasks while your team focuses on closing</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span>Provides consistent, accurate product information</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span>Generates qualified leads with detailed context</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Video Showcase Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Salesforce AI Agent Ecosystem</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Watch how our Salesforce AI Agent Ecosystem transforms your entire sales process.
            </p>
          </div>
                
          <div className="relative bg-slate-800/50 backdrop-blur-sm rounded-xl overflow-hidden shadow-xl border border-slate-700/50 max-w-5xl mx-auto">
            {/* Responsive video container with 16:9 aspect ratio */}
            <div className="relative pb-[56.25%] h-0 overflow-hidden">
              <iframe 
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/4L3ZObnioro?si=nlC7CPC376OdOcPF&rel=0&modestbranding=1"
                title="Nia AI Sales Assistant Ecosystem"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                frameBorder="0"
                allowFullScreen
              ></iframe>
            </div>
                
            {/* Optional decorative elements that match your existing design */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl"></div>
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl"></div>
          </div>
                
          <div className="mt-12 flex justify-center">
            <a 
              href="#demo" 
              onClick={() => handleChatWithNia('general')}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors flex items-center"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Try Nia Now
            </a>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6">
  <div className="max-w-7xl mx-auto">
    <div className="text-center mb-16">
      <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
      <p className="text-xl text-slate-300 max-w-3xl mx-auto">
        Everything you need to know about Nia, your AI Sales Assistant
      </p>
    </div>
    
    {/* Quick Navigation */}
    <div className="flex flex-wrap justify-center gap-4 mb-12">
      <a href="#general-questions" className="px-4 py-2 bg-slate-800/70 hover:bg-blue-500/20 border border-slate-700/50 rounded-lg text-white text-sm transition-colors">
        General Questions
      </a>
      <a href="#ai-salesforce" className="px-4 py-2 bg-slate-800/70 hover:bg-blue-500/20 border border-slate-700/50 rounded-lg text-white text-sm transition-colors">
        Salesforce AI Agent
      </a>
      <a href="#technical-questions" className="px-4 py-2 bg-slate-800/70 hover:bg-blue-500/20 border border-slate-700/50 rounded-lg text-white text-sm transition-colors">
        Technical
      </a>
      <a href="#functionality-questions" className="px-4 py-2 bg-slate-800/70 hover:bg-blue-500/20 border border-slate-700/50 rounded-lg text-white text-sm transition-colors">
        Functionality
      </a>
      <a href="#business-questions" className="px-4 py-2 bg-slate-800/70 hover:bg-blue-500/20 border border-slate-700/50 rounded-lg text-white text-sm transition-colors">
        Business & Pricing
      </a>
      <a href="#implementation-questions" className="px-4 py-2 bg-slate-800/70 hover:bg-blue-500/20 border border-slate-700/50 rounded-lg text-white text-sm transition-colors">
        Implementation
      </a>
      <a href="#future-questions" className="px-4 py-2 bg-slate-800/70 hover:bg-blue-500/20 border border-slate-700/50 rounded-lg text-white text-sm transition-colors">
        Future
      </a>
    </div>
    
    {/* FAQ Accordion */}
    <div className="max-w-4xl mx-auto space-y-12">
      {/* Category: General Questions */}
      <div id="general-questions">
        <h3 className="text-xl font-semibold mb-6 text-blue-400 flex items-center">
          <MessageSquare className="w-5 h-5 mr-2" />
          General Questions About Nia
        </h3>
        
        {faqItems?.general?.map((item, index) => (
          <FaqAccordionItem 
            key={`general-${index}`}
            question={item.question}
            answer={item.answer}
          />
        ))}
      </div>
      
      {/* Category: Nia Ecosystem */}
      <div id="ai-salesforce">
        <h3 className="text-xl font-semibold mb-6 text-blue-400 flex items-center">
          <BrainCircuit className="w-5 h-5 mr-2" />
          Salesforce AI Agent
        </h3>
        
        {faqItems?.ecosystem?.map((item, index) => (
          <FaqAccordionItem 
            key={`ecosystem-${index}`}
            question={item.question}
            answer={item.answer}
          />
        ))}
      </div>
      
      {/* Category: Technical Questions */}
      <div id="technical-questions">
        <h3 className="text-xl font-semibold mb-6 text-blue-400 flex items-center">
          <Settings className="w-5 h-5 mr-2" />
          Technical Questions
        </h3>
        
        {faqItems?.technical?.map((item, index) => (
          <FaqAccordionItem 
            key={`technical-${index}`}
            question={item.question}
            answer={item.answer}
          />
        ))}
      </div>
      
      {/* Category: Functionality */}
      <div id="functionality-questions">
        <h3 className="text-xl font-semibold mb-6 text-blue-400 flex items-center">
          <Zap className="w-5 h-5 mr-2" />
          Functionality Questions
        </h3>
        
        {faqItems?.functionality?.map((item, index) => (
          <FaqAccordionItem 
            key={`functionality-${index}`}
            question={item.question}
            answer={item.answer}
          />
        ))}
      </div>
      
      {/* Category: Business and Pricing */}
      <div id="business-questions">
        <h3 className="text-xl font-semibold mb-6 text-blue-400 flex items-center">
          <CreditCard className="w-5 h-5 mr-2" />
          Business and Pricing Questions
        </h3>
        
        {faqItems?.business?.map((item, index) => (
          <FaqAccordionItem 
            key={`business-${index}`}
            question={item.question}
            answer={item.answer}
          />
        ))}
      </div>
      
      {/* Category: Implementation */}
      <div id="implementation-questions">
        <h3 className="text-xl font-semibold mb-6 text-blue-400 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Implementation Questions
        </h3>
        
        {faqItems?.implementation?.map((item, index) => (
          <FaqAccordionItem 
            key={`implementation-${index}`}
            question={item.question}
            answer={item.answer}
          />
        ))}
      </div>
      
      {/* Category: Future Developments */}
      <div id="future-questions">
        <h3 className="text-xl font-semibold mb-6 text-blue-400 flex items-center">
          <FunctionSquare className="w-5 h-5 mr-2" />
          Future Developments
        </h3>
        
        {faqItems?.future?.map((item, index) => (
          <FaqAccordionItem 
            key={`future-${index}`}
            question={item.question}
            answer={item.answer}
          />
        ))}
      </div>
    </div>
  </div>
      </section>
      <ScrollToTopButton />
    </div>
  
  );
};

export default NiaProductPage;